import React from "react";

import AccountScoreGauge from "./AccountScoreGauge";
import type { AccountSummary } from "../../../../Store/Reducers/AccountWorkspaceSlice";
import { useAccountTiers } from "../../../../Utils/Helper/accountTierHelpers";
import { formatDateTime, getInitials } from "../../../../Utils/Helper/Helper";
import { formatPublishedAlignmentLevel } from "../../../Relationship/account-relationships/relationshipUtils";
import { formatCompactCurrencyDisplay } from "../../utils/formatCompactCurrency";
import type { AccountHawkeyeMetrics } from "../Kpis";

type MetricTone = "danger" | "success" | "info" | "default";

type MetricCell = {
  value: string | number;
  label: string;
  tone: MetricTone;
};

type AccountOverviewProps = {
  account: AccountSummary | null;
  hawkeye: AccountHawkeyeMetrics;
  className?: string;
};

const NA = "NA";

const formatRenewalDisplay = (
  renewalDate: string | null | undefined
): string => {
  if (renewalDate == null || renewalDate === "") return NA;
  return formatDateTime(renewalDate).date || NA;
};

const metricValueClass: Record<MetricTone, string> = {
  danger: "text-[#E01E21]",
  success: "text-[#01BA10]",
  info: "text-[#00B9BF]",
  default: "text-[#4F4F4F]",
};

const AccountOverview: React.FC<AccountOverviewProps> = ({
  account,
  hawkeye,
  className = "",
}) => {
  const { formatTier } = useAccountTiers();
  const accountScore = hawkeye.matrixData[0]?.digestMeta?.overall_score ?? null;

  const accountName =
    account?.account_name != null && account.account_name !== ""
      ? account.account_name
      : NA;
  const city = account?.city != null && account.city !== "" ? account.city : NA;
  const country =
    account?.country != null && account.country !== "" ? account.country : NA;
  const region =
    account?.region != null && account.region !== "" ? account.region : NA;
  const industry =
    account?.industry != null && account.industry !== ""
      ? account.industry
      : NA;

  const metricRows: MetricCell[][] = [
    [
      {
        value: formatRenewalDisplay(account?.renewal_date),
        label: "Renewal",
        tone: "danger",
      },
      {
        value: formatCompactCurrencyDisplay(account?.revenue),
        label: "Revenue",
        tone: "success",
      },
      {
        value:
          account?.tier != null && account.tier !== ""
            ? formatTier(account.tier)
            : NA,
        label: "Tier",
        tone: "default",
      },
    ],
    [
      { value: NA, label: "Risks", tone: "danger" },
      { value: NA, label: "Opportunities", tone: "success" },
      { value: NA, label: "Sentiments", tone: "info" },
    ],
    [
      {
        value: formatPublishedAlignmentLevel(account?.assessment_level),
        label: "AL Score",
        tone: "default",
      },
      { value: "RL ?", label: "RL Score", tone: "default" },
      { value: "RL ?", label: "My RL Score", tone: "default" },
    ],
  ];

  return (
    <section
      className={`box-border flex h-full w-full min-w-0 flex-col overflow-hidden rounded-lg border border-[#EBEBEB] bg-white px-4 pb-3 pt-3.5 ${className}`}
    >
      <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col gap-3">
        <h2 className="m-0 shrink-0 min-h-6 text-base font-bold leading-6 text-black">
          Account Overview
        </h2>

        <hr className="m-0 w-full shrink-0 border-0 border-t border-[#F6F6F6]" />

        <div className="flex w-full shrink-0 items-start justify-between gap-2.5 rounded-lg bg-[#F9FAFB] p-2.5">
          <div className="flex min-w-0 items-center gap-4">
            <div
              className="flex size-[61px] shrink-0 items-center justify-center rounded-full border-2 border-[#D1D1D1] bg-white text-sm font-semibold text-[#9F9F9F] shadow-[0px_0px_10px_rgba(209,209,209,0.25)]"
              aria-hidden
            >
              {accountName !== NA ? getInitials(accountName) : "—"}
            </div>

            <div className="flex min-w-0 flex-col gap-1.5">
              <h3 className="m-0 truncate text-sm font-bold leading-[17px] text-[#455169]">
                {accountName}
              </h3>
              <p className="m-0 text-xs font-medium leading-[15px] text-[#9F9F9F]">
                {city}, {country}. - {region}
              </p>
              <p className="m-0 text-xs font-medium leading-[15px] text-[#9F9F9F]">
                Industry: {industry}
              </p>
            </div>
          </div>

          <AccountScoreGauge score={hawkeye.loading ? null : accountScore} />
        </div>

        <hr className="m-0 w-full shrink-0 border-0 border-t border-[#F6F6F6]" />

        <p className="m-0 shrink-0 text-[13px] font-bold leading-4 text-[#455169]">
          Key Details:
        </p>

        <div className="flex min-h-0 w-full flex-1 flex-col gap-2.5">
          {metricRows.map(row => (
            <div
              key={row[0].label}
              className="flex min-h-0 w-full flex-1 gap-2.5"
            >
              {row.map(metric => (
                <div
                  key={metric.label}
                  className="box-border flex min-h-[55px] min-w-0 flex-1 flex-col justify-center gap-1 rounded-lg border border-[#E8EAEB] px-4 py-2"
                >
                  <span
                    className={`w-full truncate text-lg font-semibold leading-[22px] tracking-tight ${metricValueClass[metric.tone]}`}
                    title={
                      /^(AL|RL)\s*\?$/i.test(String(metric.value).trim())
                        ? "Unassessed"
                        : String(metric.value)
                    }
                  >
                    {metric.value}
                  </span>
                  <span className="text-xs font-medium leading-[15px] text-[#959595]">
                    {metric.label}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AccountOverview;
